import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, FileText, Calendar, AlertTriangle, Shield, Scale, Mail, Clock, Link } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const TermsPage = () => {
  const [openSections, setOpenSections] = useState({
    acceptance: true,
    content: false,
    affiliate: false,
    intellectual: false,
    liability: false,
    changes: false,
    governing: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Terms of Service
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                Rules and guidelines for using Radiance
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* 1. Acceptance of Terms */}
          <AccordionItem
            title="1. Acceptance of Terms"
            isOpen={openSections.acceptance}
            onToggle={() => toggleSection('acceptance')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                By accessing or using Radiance (the "Service"), you agree to be bound by these Terms of Service ("Terms"), along with our <a href="/privacy" className="text-[#ab3d22] hover:underline">Privacy Policy</a>, <a href="/editorial-standards" className="text-[#ab3d22] hover:underline">Editorial Standards</a>, and <a href="/affiliate-disclosure" className="text-[#ab3d22] hover:underline">Affiliate Disclosure</a>.
              </p>
              <p className="text-sm text-gray-600">
                If you do not agree, you must stop using the Service immediately.
              </p>
            </div>
          </AccordionItem>

          {/* 2. Use of Content */}
          <AccordionItem
            title="2. Use of Content"
            isOpen={openSections.content}
            onToggle={() => toggleSection('content')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Informational Purposes Only</h4>
                <p className="text-sm text-gray-600">
                  Radiance provides beauty and wellness content for general informational and educational purposes. Our insights are designed to help you navigate the beauty world more confidently, but they do not replace professional, medical, dermatological, or therapeutic advice.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Not Medical Advice</h4>
                <p className="text-sm text-gray-600">
                  Always consult a qualified healthcare provider for decisions related to your skin, health, allergies, sensitivities, or treatment plans.
                </p>
              </div>

              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Individual Results Vary</p>
                    <p className="text-sm">
                      Product performance differs from person to person. We do not guarantee outcomes or results.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 3. Affiliate Relationships */}
          <AccordionItem
            title="3. Affiliate Relationships"
            isOpen={openSections.affiliate}
            onToggle={() => toggleSection('affiliate')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance participates in affiliate programs and may earn a commission when you click certain links and complete a purchase. This commission does not affect the price you pay.
              </p>
              
              <h4 className="font-semibold text-gray-900">Our commitments:</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• We feature products because we believe they offer value</li>
                <li>• Editorial decisions are independent from affiliate partnerships</li>
                <li>• All applicable affiliate disclosures are clearly stated</li>
                <li>• We comply with advertising and disclosure regulations</li>
                <li>• Commissions never determine rankings or recommendations</li>
              </ul>
              
              <p className="text-sm text-gray-600">
                Affiliate income helps keep Radiance free and supports ongoing content creation.
              </p>
            </div>
          </AccordionItem>

          {/* 4. Intellectual Property */}
          <AccordionItem
            title="4. Intellectual Property"
            isOpen={openSections.intellectual}
            onToggle={() => toggleSection('intellectual')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Ownership of Content</h4>
                <p className="text-sm text-gray-600">
                  Unless otherwise credited, all text, images, graphics, video, branding, and other materials on Radiance are the property of Radiance Inc. and protected under copyright, trademark, and intellectual property laws.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Limited License</h4>
                <p className="text-sm text-gray-600">
                  You may share or link to our content for personal, non-commercial use. You may not reproduce, modify, distribute, republish, or create derivative works without our express written permission.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Third-Party Content</h4>
                <p className="text-sm text-gray-600">
                  Some content (such as product images, retailer assets, or brand materials) may be sourced from third parties. Rights remain with their respective owners.
                </p>
              </div>
            </div>
          </AccordionItem>

          {/* 5. Limitation of Liability */}
          <AccordionItem
            title="5. Limitation of Liability"
            isOpen={openSections.liability}
            onToggle={() => toggleSection('liability')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance provides the Service on an "as is" and "as available" basis without warranties of any kind.
              </p>
              
              <p className="text-sm text-gray-600 font-medium">By using the Service, you agree that:</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Radiance is not responsible for decisions you make based on our content</li>
                <li>• We do not guarantee accuracy, completeness, or reliability of information</li>
                <li>• We are not responsible for the performance, quality, or safety of third-party products</li>
                <li>• Product outcomes vary by individual and are not guaranteed</li>
              </ul>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Scale className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Disclaimer</p>
                    <p className="text-sm">
                      Radiance Inc. is not liable for any direct, indirect, incidental, special, or consequential damages arising from your use of our content, reliance on recommendations, or purchase/use of third-party products.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 6. Changes to Terms */}
          <AccordionItem
            title="6. Changes to Terms"
            isOpen={openSections.changes}
            onToggle={() => toggleSection('changes')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We may update these Terms periodically. The "Last Updated" date reflects the most recent revision. Continued use of Radiance after updates signifies your acceptance of the revised Terms.
              </p>
            </div>
          </AccordionItem>

          {/* 7. Governing Law */}
          <AccordionItem
            title="7. Governing Law"
            isOpen={openSections.governing}
            onToggle={() => toggleSection('governing')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                These Terms are governed by the laws of Ontario, Canada, without regard to conflict-of-law principles. Any disputes arising from the Service will be resolved under these laws.
              </p>
            </div>
          </AccordionItem>

          {/* 8. Contact Us */}
          <AccordionItem
            title="8. Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If you have questions about these Terms or need assistance, please contact us:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#ab3d22]" />
                  <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                    hello@meetradiance.com
                  </a>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default TermsPage;
